import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { authService } from './auth'
import { PackageCard, Package, PackageList } from '../types/package'
import { User, UserList, UserListResponse, UserStats, UserSearchFilters, UserUpdate, UserAdminUpdate } from '../types/user'

// API configuration
const finalBaseURL = window.location.hostname === 'shop.nickfixit.com'
  ? 'https://api.nickfixit.com'
  : (import.meta.env.VITE_API_BASE_URL || `${window.location.protocol}//${window.location.hostname}:8000`)

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: finalBaseURL,
  timeout: 30000,
  withCredentials: true, // Include cookies in requests
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor (no token needed since using cookies)
apiClient.interceptors.request.use(
  async (config) => {
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Authentication failed, redirect to login
      authService.login()
    }
    return Promise.reject(error)
  }
)

// API client wrapper
class ApiClient {
  private client: AxiosInstance

  constructor(client: AxiosInstance) {
    this.client = client
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config)
    return response.data
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config)
    return response.data
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config)
    return response.data
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config)
    return response.data
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config)
    return response.data
  }
}

const client = new ApiClient(apiClient)

// Package API
export const packagesApi = {
  // Get paginated packages
  getPackages: (params?: {
    page?: number
    size?: number
    package_type?: string
    billing_type?: string
    is_featured?: boolean
    search?: string
  }): Promise<PackageList> => {
    return client.get('/api/packages', { params })
  },

  // Get package cards for listings
  getPackageCards: (params?: {
    package_type?: string
    featured_only?: boolean
    limit?: number
  }): Promise<PackageCard[]> => {
    return client.get('/api/packages/cards', { params })
  },

  // Get featured packages
  getFeatured: (limit = 3): Promise<PackageCard[]> => {
    return client.get('/api/packages/featured', { params: { limit } })
  },

  // Get package by ID
  getPackageById: (id: string): Promise<Package> => {
    return client.get(`/api/packages/${id}`)
  },

  // Get package by slug
  getPackageBySlug: (slug: string): Promise<Package> => {
    return client.get(`/api/packages/slug/${slug}`)
  },

  // Get deployment template for package
  getDeploymentTemplate: (id: string): Promise<any> => {
    return client.get(`/api/packages/${id}/deployment-template`)
  },
}

// Auth API
export const authApi = {
  // Get current user profile
  getProfile: (): Promise<any> => {
    return client.get('/api/auth/profile')
  },

  // Update user profile
  updateProfile: (data: any): Promise<any> => {
    return client.put('/api/auth/profile', data)
  },
}

// Orders API
export const ordersApi = {
  // Get user orders
  getOrders: (params?: {
    page?: number
    size?: number
    status?: string
  }): Promise<any> => {
    return client.get('/api/orders', { params })
  },

  // Get order by ID
  getOrderById: (id: string): Promise<any> => {
    return client.get(`/api/orders/${id}`)
  },

  // Create new order
  createOrder: (data: any): Promise<any> => {
    return client.post('/api/orders', data)
  },
}

// Billing API
export const billingApi = {
  // Get billing information
  getBillingInfo: (): Promise<any> => {
    return client.get('/api/billing/info')
  },

  // Get invoices
  getInvoices: (params?: {
    page?: number
    size?: number
  }): Promise<any> => {
    return client.get('/api/billing/invoices', { params })
  },

  // Create payment intent
  createPaymentIntent: (data: {
    package_id: string
    billing_type: string
  }): Promise<any> => {
    return client.post('/api/billing/payment-intent', data)
  },

  // Create subscription
  createSubscription: (data: {
    package_id: string
    payment_method_id: string
  }): Promise<any> => {
    return client.post('/api/billing/subscription', data)
  },

  // Get customer portal URL
  getCustomerPortalUrl: (): Promise<{ url: string }> => {
    return client.post('/api/billing/customer-portal')
  },
}

// Services API (for managing deployed services)
export const servicesApi = {
  // Get user services
  getServices: (): Promise<any> => {
    return client.get('/api/services')
  },

  // Get service by ID
  getServiceById: (id: string): Promise<any> => {
    return client.get(`/api/services/${id}`)
  },

  // Get service status
  getServiceStatus: (id: string): Promise<any> => {
    return client.get(`/api/services/${id}/status`)
  },

  // Get service logs
  getServiceLogs: (id: string, params?: {
    lines?: number
    since?: string
  }): Promise<any> => {
    return client.get(`/api/services/${id}/logs`, { params })
  },

  // Restart service
  restartService: (id: string): Promise<any> => {
    return client.post(`/api/services/${id}/restart`)
  },
}

// Users API (Admin only)
export const usersApi = {
  // Get paginated list of users
  getUsers: (filters?: UserSearchFilters): Promise<UserListResponse> => {
    return client.get('/api/users/', { params: filters })
  },

  // Get user statistics
  getUserStats: (): Promise<UserStats> => {
    return client.get('/api/users/admin/stats')
  },

  // Get user details by ID (admin)
  getUserById: (userId: string): Promise<User> => {
    return client.get(`/api/users/admin/${userId}`)
  },

  // Update user (admin)
  updateUser: (userId: string, data: UserAdminUpdate): Promise<User> => {
    return client.put(`/api/users/admin/${userId}`, data)
  },

  // Deactivate user
  deactivateUser: (userId: string): Promise<{ message: string }> => {
    return client.delete(`/api/users/admin/${userId}`)
  },

  // Permanently delete user
  permanentlyDeleteUser: (userId: string): Promise<{ message: string }> => {
    return client.delete(`/api/users/admin/${userId}/permanent`)
  },

  // Reactivate user
  reactivateUser: (userId: string): Promise<{ message: string }> => {
    return client.post(`/api/users/admin/${userId}/reactivate`)
  },

  // Verify user
  verifyUser: (userId: string): Promise<{ message: string }> => {
    return client.post(`/api/users/admin/${userId}/verify`)
  },

  // Make user admin
  makeUserAdmin: (userId: string): Promise<{ message: string }> => {
    return client.post(`/api/users/admin/${userId}/make-admin`)
  },

  // Revoke user admin
  revokeUserAdmin: (userId: string): Promise<{ message: string }> => {
    return client.post(`/api/users/admin/${userId}/revoke-admin`)
  },
}

// Toolkits API
export const toolkitsApi = {
  // Get all toolkits
  getToolkits: (params?: {
    category?: string
    featured?: boolean
  }): Promise<any[]> => {
    return client.get('/api/toolkits', { params })
  },

  // Get toolkit categories
  getCategories: (): Promise<any[]> => {
    return client.get('/api/toolkits/categories')
  },

  // Get toolkit by ID
  getToolkitById: (id: string): Promise<any> => {
    return client.get(`/api/toolkits/${id}`)
  },

  // Get toolkit health
  getToolkitHealth: (id: string): Promise<any> => {
    return client.get(`/api/toolkits/${id}/health`)
  },

  // Call toolkit tool
  callTool: (toolkitId: string, toolName: string, args: any): Promise<any> => {
    return client.post(`/api/toolkits/${toolkitId}/${toolName}`, args)
  },
}

// Package Builder API
export const packageBuilderApi = {
  // Get compatible toolkits for a package type
  getCompatibleToolkits: (packageType: string): Promise<any[]> => {
    return client.get(`/api/package-builder/toolkit-addons/${packageType}`)
  },

  // Get available custom services
  getCustomServices: (): Promise<any[]> => {
    return client.get('/api/package-builder/custom-services')
  },

  // Get physical products
  getPhysicalProducts: (category?: string): Promise<any[]> => {
    return client.get('/api/package-builder/physical-products', {
      params: category ? { category } : undefined
    })
  },

  // Calculate pricing for custom package
  calculatePricing: (customization: any): Promise<any> => {
    return client.post('/api/package-builder/calculate-pricing', customization)
  },

  // Create package quote
  createQuote: (customization: any): Promise<any> => {
    return client.post('/api/package-builder/create-quote', customization)
  },

  // Get specific quote
  getQuote: (quoteId: string): Promise<any> => {
    return client.get(`/api/package-builder/quotes/${quoteId}`)
  },

  // Get user quotes
  getUserQuotes: (): Promise<any[]> => {
    return client.get('/api/package-builder/user-quotes')
  },

  // Order from quote
  orderFromQuote: (quoteId: string): Promise<any> => {
    return client.post(`/api/package-builder/quotes/${quoteId}/order`)
  },
}

// Health check
export const healthApi = {
  getHealth: (): Promise<any> => {
    return client.get('/health')
  },
}

// Raw client for admin operations
export const rawApi = client

// Unified Purchase API
export const purchaseApi = {
  // Unified purchase endpoint
  unifiedPurchase: (data: {
    purchase_mode: 'checkout' | 'subscribe' | 'quote'
    item_type: 'product' | 'package' | 'custom_package'
    item_id: string
    quantity?: number
    customizations?: any
    shipping_address?: any
    payment_method?: string
    billing_cycle?: string
    metadata?: any
  }): Promise<{
    purchase_id: string
    purchase_mode: string
    purchase_type: string
    payment_intent_id?: string
    subscription_id?: string
    quote_id?: string
    client_secret?: string
    item_name: string
    item_description?: string
    total_amount: number
    currency: string
    recurring_amount?: number
    setup_fee?: number
    shipping_required: boolean
    estimated_delivery?: string
    fulfillment_type: string
    status: string
    next_step: string
    next_step_url?: string
    order_id?: string
    expires_at?: string
    metadata?: any
  }> => {
    return client.post('/api/payments/unified-purchase', data)
  },

  // Get purchase status
  getPurchaseStatus: (purchaseId: string): Promise<any> => {
    return client.get(`/api/payments/purchases/${purchaseId}/status`)
  },

  // Simple product checkout (legacy)
  simpleCheckout: (data: {
    product_id: string
    quantity?: number
    shipping_address?: any
  }): Promise<any> => {
    return client.post('/api/payments/simple-checkout', data)
  },
}

// Aggregate API object
export const api = {
  packages: packagesApi,
  auth: authApi,
  orders: ordersApi,
  billing: billingApi,
  services: servicesApi,
  users: usersApi,
  toolkits: toolkitsApi,
  packageBuilder: packageBuilderApi,
  purchase: purchaseApi,
  health: healthApi,
  // Add raw client methods for admin
  get: client.get.bind(client),
  post: client.post.bind(client),
  put: client.put.bind(client),
  patch: client.patch.bind(client),
  delete: client.delete.bind(client),
}

export default api